import { toISODate } from "@todoer/shared";

export const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return toISODate(d);
};

export const weekStartOf = (iso: string, startOfWeek: number) => {
  const day = new Date(`${iso}T00:00:00`).getDay();
  return addDays(iso, -((day - startOfWeek + 7) % 7));
};

export interface HabitProgress {
  /** current streak in days (daily habits) or weeks (weekly targets) */
  streak: number;
  best: number;
  unit: "day" | "week";
  thisWeek: number;
  total: number;
}

/**
 * Daily habits (target 7) count consecutive checked days; a streak survives until the end
 * of today. Habits with a lower weekly target count consecutive weeks that met the target;
 * the current week only adds to the streak once its target is met.
 */
export function habitProgress(dates: Iterable<string>, targetPerWeek: number, today: string, startOfWeek = 1): HabitProgress {
  const set = new Set(dates);
  const thisWeekStart = weekStartOf(today, startOfWeek);
  let thisWeek = 0;
  for (let i = 0; i < 7; i++) if (set.has(addDays(thisWeekStart, i))) thisWeek++;
  const sorted = [...set].sort();
  if (!sorted.length) return { streak: 0, best: 0, unit: targetPerWeek >= 7 ? "day" : "week", thisWeek, total: 0 };

  if (targetPerWeek >= 7) {
    let streak = 0;
    let d = set.has(today) ? today : addDays(today, -1);
    while (set.has(d)) { streak++; d = addDays(d, -1); }
    let best = 0;
    let run = 0;
    let prev: string | null = null;
    for (const x of sorted) { run = prev && addDays(prev, 1) === x ? run + 1 : 1; best = Math.max(best, run); prev = x; }
    return { streak, best, unit: "day", thisWeek, total: set.size };
  }

  const perWeek = new Map<string, number>();
  for (const x of sorted) { const w = weekStartOf(x, startOfWeek); perWeek.set(w, (perWeek.get(w) ?? 0) + 1); }
  const met = (w: string) => (perWeek.get(w) ?? 0) >= targetPerWeek;
  let streak = 0;
  let w = met(thisWeekStart) ? thisWeekStart : addDays(thisWeekStart, -7);
  while (met(w)) { streak++; w = addDays(w, -7); }
  let best = 0;
  let run = 0;
  for (let wk = weekStartOf(sorted[0]!, startOfWeek); wk <= thisWeekStart; wk = addDays(wk, 7)) {
    run = met(wk) ? run + 1 : 0;
    best = Math.max(best, run);
  }
  return { streak, best, unit: "week", thisWeek, total: set.size };
}
